import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { Home, TreePine, Calendar, Loader2, CheckCircle } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Label } from '../ui/label';
import { toast } from 'sonner';
import { boxesTarifs, prestationsHaras } from './venirData';

const API = `${window.location.origin}/api`;

export default function HarasTab() {
  const { t } = useTranslation();
  const [reservationForm, setReservationForm] = useState({
    name: '', email: '', phone: '', horse_name: '',
    reservation_type: 'box', start_date: '', end_date: '',
    number_of_horses: 1, message: ''
  });
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleReservationSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/horse-reservations`, reservationForm);
      toast.success(t('venir.msgSent'), { description: t('venir.msgSentDesc') });
      setSubmitted(true);
      setReservationForm({ name: '', email: '', phone: '', horse_name: '', reservation_type: 'box', start_date: '', end_date: '', number_of_horses: 1, message: '' });
    } catch (error) {
      toast.error(t('venir.msgError'), { description: t('venir.msgErrorDesc') });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="haras-tab">
      <div className="mb-8">
        <h2 className="font-['Playfair_Display'] text-2xl font-bold text-zinc-900 mb-2">
          Hébergement Chevaux - <span className="text-fuchsia-600">Haras de la Briouse</span>
        </h2>
        <p className="text-zinc-600"><strong>9 boxes disponibles</strong> et <strong>7 paddocks</strong> pour accueillir vos chevaux</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        <div className="space-y-6">
          <Card className="border-0 shadow-lg" data-testid="boxes-tarifs">
            <CardHeader><CardTitle className="flex items-center gap-2"><Home className="h-5 w-5 text-fuchsia-500" />Hébergement Mensuel</CardTitle></CardHeader>
            <CardContent>
              <div className="space-y-4">
                {boxesTarifs.map((tarif, index) => (
                  <div key={index} className="flex justify-between items-center p-3 bg-zinc-50 rounded-lg">
                    <div><p className="font-medium text-zinc-900">{tarif.type}</p><p className="text-sm text-zinc-500">{tarif.details}</p></div>
                    <span className="font-bold text-fuchsia-600">{tarif.price}</span>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
          <Card className="border-0 shadow-lg" data-testid="prestations-haras">
            <CardHeader><CardTitle className="flex items-center gap-2"><TreePine className="h-5 w-5 text-lime-500" />Prestations</CardTitle></CardHeader>
            <CardContent>
              <div className="space-y-3">
                {prestationsHaras.map((p, index) => (
                  <div key={index} className="flex justify-between items-center p-3 bg-zinc-50 rounded-lg">
                    <div><p className="font-medium text-zinc-900">{p.name}</p>{p.details && <p className="text-sm text-zinc-500">{p.details}</p>}</div>
                    <span className="font-bold text-lime-600">{p.price}</span>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </div>

        <Card className="border-0 shadow-xl" data-testid="reservation-form">
          <CardHeader className="bg-gradient-to-r from-fuchsia-500 to-fuchsia-600 text-white rounded-t-lg">
            <CardTitle className="flex items-center gap-2"><Calendar className="h-5 w-5" />Pré-réservation Box / Paddock</CardTitle>
          </CardHeader>
          <CardContent className="p-6">
            {submitted ? (
              <div className="text-center py-8">
                <CheckCircle className="h-16 w-16 text-lime-500 mx-auto mb-4" />
                <h3 className="text-xl font-bold text-zinc-900 mb-2">Demande envoyée !</h3>
                <p className="text-zinc-600 mb-4">Nous vous contacterons rapidement pour confirmer votre réservation.</p>
                <Button onClick={() => setSubmitted(false)} variant="outline">Nouvelle demande</Button>
              </div>
            ) : (
              <form onSubmit={handleReservationSubmit} className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div><Label htmlFor="name">Nom complet *</Label><Input id="name" value={reservationForm.name} onChange={(e) => setReservationForm({...reservationForm, name: e.target.value})} required data-testid="reservation-name" /></div>
                  <div><Label htmlFor="phone">Téléphone *</Label><Input id="phone" value={reservationForm.phone} onChange={(e) => setReservationForm({...reservationForm, phone: e.target.value})} required data-testid="reservation-phone" /></div>
                </div>
                <div><Label htmlFor="email">Email *</Label><Input id="email" type="email" value={reservationForm.email} onChange={(e) => setReservationForm({...reservationForm, email: e.target.value})} required data-testid="reservation-email" /></div>
                <div className="grid grid-cols-2 gap-4">
                  <div><Label htmlFor="horse_name">Nom du cheval *</Label><Input id="horse_name" value={reservationForm.horse_name} onChange={(e) => setReservationForm({...reservationForm, horse_name: e.target.value})} required data-testid="reservation-horse" /></div>
                  <div><Label htmlFor="number_of_horses">Nombre de chevaux</Label><Input id="number_of_horses" type="number" min="1" max="9" value={reservationForm.number_of_horses} onChange={(e) => setReservationForm({...reservationForm, number_of_horses: parseInt(e.target.value)})} data-testid="reservation-nb-horses" /></div>
                </div>
                <div>
                  <Label>Type d'hébergement *</Label>
                  <div className="grid grid-cols-2 gap-2 mt-2">
                    <Button type="button" variant={reservationForm.reservation_type === 'box' ? 'default' : 'outline'} className={reservationForm.reservation_type === 'box' ? 'bg-fuchsia-600' : ''} onClick={() => setReservationForm({...reservationForm, reservation_type: 'box'})} data-testid="reservation-type-box">Box (9 dispo)</Button>
                    <Button type="button" variant={reservationForm.reservation_type === 'paddock' ? 'default' : 'outline'} className={reservationForm.reservation_type === 'paddock' ? 'bg-lime-600' : ''} onClick={() => setReservationForm({...reservationForm, reservation_type: 'paddock'})} data-testid="reservation-type-paddock">Paddock (7 dispo)</Button>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div><Label htmlFor="start_date">Date d'arrivée *</Label><Input id="start_date" type="date" value={reservationForm.start_date} onChange={(e) => setReservationForm({...reservationForm, start_date: e.target.value})} required data-testid="reservation-start" /></div>
                  <div><Label htmlFor="end_date">Date de départ *</Label><Input id="end_date" type="date" value={reservationForm.end_date} onChange={(e) => setReservationForm({...reservationForm, end_date: e.target.value})} required data-testid="reservation-end" /></div>
                </div>
                <div><Label htmlFor="message">Message (optionnel)</Label><Textarea id="message" rows={3} value={reservationForm.message} onChange={(e) => setReservationForm({...reservationForm, message: e.target.value})} placeholder="Précisions sur votre demande..." data-testid="reservation-message" /></div>
                <Button type="submit" className="w-full bg-fuchsia-600 hover:bg-fuchsia-700 text-white" disabled={loading} data-testid="reservation-submit">
                  {loading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}Envoyer la demande
                </Button>
              </form>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
